import React from 'react';
import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { Box } from '@mui/material';
import {
  ContextMenu,
  EnhancedTableBody,
  EnhancedTableContainer,
  EnhancedTableFooter,
  EnhancedTableHead,
  EnhancedTablePagination,
  EnhancedTableRow,
  TableBulkActionsSection,
  TableErrorState,
  TableFiltersSection,
  TableHeaderSection,
  TableLoadingSkeleton,
} from './components';
import { TableFooterSection } from './components/sections/table-footer-section';
import { TableProvider } from './context';
import { useTableOrchestrator } from './hooks';
import { EnhancedTableProps } from './types';

/**
 * Enhanced Table Component
 *
 * A powerful, feature-rich table built with React and Material‑UI that acts as an
 * orchestration shell. All heavy logic (sorting, pagination, filtering, selection,
 * expansion, keyboard nav, etc.) lives in `useTableOrchestrator`, while the visual
 * parts are composed from focused sections.
 *
 * ## Design & SOLID
 * - Single Responsibility: This component orchestrates sections and forwards
 *   handlers/state coming from `useTableOrchestrator`. Sections (`Header`,
 *   `Filters`, `Body`, `Footer`, `Pagination`, `ContextMenu`) render UI.
 * - Open/Closed: Features are opt‑in via props; adding a feature is usually a
 *   new section/hook without modifying this file.
 * - Interface Segregation: Callers provide only the props they need (e.g., pass
 *   `pagination`, `sorting`, `footer` selectively).
 * - Dependency Inversion: The view depends on context/hook abstractions, not
 *   implementations.
 *
 * ## UI/UX Features
 * - Sticky header, dense mode, column visibility, resizing, reordering
 * - Collapsible rows, inline editing, keyboard navigation
 * - Client & server‑side data flows
 *
 * ## Sticky Footer
 * - Within-table footer: set `footer={{ enabled: true, position: 'within-table' }}`.
 *   Use `footer.sticky` to pin it while scrolling inside the container.
 * - Separate footer: set `footer={{ enabled: true, position: 'separate' }}`.
 *   When `footer.sticky` is true, the separate footer is rendered just under the
 *   table inside the same scroll container and stays visible while scrolling.
 *
 * The component is kept small and composable; avoid placing business logic here.
 * Prefer adding logic to the relevant hook/section.
 *
 * ## Basic Usage
 *
 * ```tsx
 * import { EnhancedTable } from '@nova-ui/components';
 *
 * const columns = [
 *   { id: 'name', label: 'Name', sortable: true },
 *   { id: 'email', label: 'Email', sortable: true },
 *   { id: 'role', label: 'Role', sortable: true }
 * ];
 *
 * const data = [
 *   { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin' },
 *   { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'User' }
 * ];
 *
 * function MyTable() {
 *   return (
 *     <EnhancedTable
 *       columns={columns}
 *       rows={data}
 *       getRowId={(row) => row.id}
 *     />
 *   );
 * }
 * ```
 *
 * ## Advanced Usage
 *
 * ```tsx
 * function AdvancedTable() {
 *   return (
 *     <EnhancedTable
 *       columns={columns}
 *       rows={data}
 *       // Pagination
 *       pagination={{
 *         page: 0,
 *         rowsPerPage: 10,
 *         count: data.length,
 *         onPageChange: handlePageChange,
 *         onRowsPerPageChange: handleRowsPerPageChange
 *       }}
 *       // Sorting
 *       sorting={{
 *         orderBy: 'name',
 *         order: 'asc',
 *         onRequestSort: handleSort
 *       }}
 *       // Selection
 *       selectedRows={selectedRows}
 *       onSelect={handleSelect}
 *       onSelectAll={handleSelectAll}
 *       // Features
 *       showHeader={true}
 *       showFilters={true}
 *       stickyHeader={true}
 *       dense={false}
 *       // Inline editing
 *       inlineEdit={{
 *         mode: 'cell',
 *         triggerMode: 'doubleClick',
 *         onCellEdit: handleCellEdit
 *       }}
 *       // Keyboard navigation
 *       keyboardNavigation={{
 *         enabled: true,
 *         selectOnEnter: true,
 *         editOnEnter: true
 *       }}
 *       // Bulk actions
 *       bulkActions={[
 *         {
 *           id: 'delete',
 *           label: 'Delete Selected',
 *           action: handleDelete
 *         }
 *       ]}
 *     />
 *   );
 * }
 * ```
 *
 * ## Server-Side Usage
 *
 * ```tsx
 * function ServerSideTable() {
 *   const fetcher = async (params) => {
 *     const response = await fetch('/api/data', {
 *       method: 'POST',
 *       body: JSON.stringify(params)
 *     });
 *     return response.json();
 *   };
 *
 *   return (
 *     <EnhancedTable
 *       columns={columns}
 *       rows={[]} // Empty - server manages data
 *       serverSide={true}
 *       serverDataFetcher={fetcher}
 *       serverSideConfig={{
 *         debounceMs: 300,
 *         initialParams: {
 *           pagination: { page: 0, pageSize: 10 }
 *         }
 *       }}
 *     />
 *   );
 * }
 * ```
 *
 * @template T - The type of data rows in the table. Must extend Record<string, unknown>
 * @param props - Configuration props for the table
 * @returns JSX.Element - The rendered table component
 *
 * @example
 * // Simple table
 * <EnhancedTable columns={columns} rows={data} />
 *
 * @example
 * // Table with pagination and sorting
 * <EnhancedTable
 *   columns={columns}
 *   rows={data}
 *   pagination={paginationConfig}
 *   sorting={sortingConfig}
 * />
 *
 * @example
 * // Server-side table
 * <EnhancedTable
 *   columns={columns}
 *   rows={[]}
 *   serverSide={true}
 *   serverDataFetcher={fetchData}
 * />
 *
 * @see {@link EnhancedTableProps} for all available props
 * @see {@link EnhancedTableColumn} for column configuration
 * @see {@link TableContext} for context API
 *
 * @since 1.0.0
 * @version 2.0.0 - Refactored with composition pattern and SOLID principles
 */
export function EnhancedTable<T extends Record<string, unknown>>(
  props: EnhancedTableProps<T>,
) {
  const {
    containerProps,
    tableProps,
    headerProps,
    buttonConfig,
    customButtons,
    title,
    filterConfigs,
    bulkActions,
    contextMenu = [],
    columnReorder = false,
    customLoadingComponent,
    customErrorComponent,
    onRetry,
    renderExpandedContent,
    keyboardNavigation,
    footer,
    'aria-label': ariaLabel = 'enhanced table',
    collapsible = false,
    isFullscreen = false,
    ...restProps
  } = props;

  // Orchestrate all table state and logic
  const tableContext = useTableOrchestrator(props);
  const {
    orderedColumns,
    paginatedRows,
    selection,
    expansion,
    columnResize,
    columnReorder: columnReorderHook,
    inlineEdit,
    keyboardNavigation: keyboardNav,
    isLoading,
    error,
    getRowId,
    onPageChange,
    onRowsPerPageChange,
    visibleColumns,
    dense,
    stickyHeader,
  } = tableContext;

  // Extract tableLayout from tableProps for width calculation logic
  const tableLayout =
    (tableProps?.sx as { tableLayout?: 'auto' | 'fixed' })?.tableLayout ||
    'auto';

  // Context menu state
  const [contextMenuAnchor, setContextMenuAnchor] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [contextMenuRow, setContextMenuRow] = React.useState<T | null>(null);
  const [contextMenuRowId, setContextMenuRowId] = React.useState<
    string | number | null
  >(null);

  /**
   * Handles column drag and drop reordering.
   * Called when a user drags a column header to a new position.
   *
   * @param event - The drag end event from dnd-kit
   */
  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      // Find the indices of the active and over columns
      const oldIndex = orderedColumns.findIndex(
        (col) => String(col.id) === active.id,
      );
      const newIndex = orderedColumns.findIndex(
        (col) => String(col.id) === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        // Use the column reorder hook directly
        columnReorderHook.handleColumnReorder(oldIndex, newIndex);
      }
    }
  };

  /**
   * Handles right-click context menu on table rows.
   * Shows context menu if contextMenu items are provided.
   *
   * @param event - The mouse event
   * @param row - The row data that was right-clicked
   * @param rowId - The unique identifier for the row
   */
  const handleContextMenu = (
    event: React.MouseEvent,
    row: T,
    rowId: string | number,
  ) => {
    if (contextMenu.length > 0) {
      event.preventDefault();
      setContextMenuAnchor({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      });
      setContextMenuRow(row);
      setContextMenuRowId(rowId);
    }
  };

  /**
   * Closes the context menu by clearing the anchor position and selected row.
   */
  const handleContextMenuClose = () => {
    setContextMenuAnchor(null);
    setContextMenuRow(null);
    setContextMenuRowId(null);
  };

  /**
   * Main table content wrapped in TableProvider context.
   * This provides all table state and handlers to child components
   * through React Context, enabling clean separation of concerns.
   */
  const tableContent = (
    <TableProvider value={tableContext}>
      {/* Header Section - Search input and column visibility controls */}
      <TableHeaderSection
        headerProps={{
          ...headerProps,
          buttonConfig,
          customButtons,
          title,
        }}
      />

      {/* Bulk Actions Section - Actions for selected rows */}
      <TableBulkActionsSection bulkActions={bulkActions} />

      {/* Filters Section - Advanced filtering UI */}
      <TableFiltersSection filterConfigs={filterConfigs} />

      {/* Main Table Container - Handles scrolling, sticky headers, and table layout */}
      <EnhancedTableContainer
        containerProps={containerProps}
        tableProps={tableProps}
        stickyHeader={stickyHeader}
        dense={dense}
        ariaLabel={ariaLabel}
        {...restProps}
        afterTable={
          footer?.enabled && footer.sticky && footer.position === 'separate' ? (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: (t) => t.zIndex.appBar - 1,
                backgroundColor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <TableFooterSection
                rows={tableContext.actualRows}
                config={footer}
              />
            </Box>
          ) : undefined
        }
      >
        {/* Table Header - Column headers with sorting, selection, and reordering */}
        <EnhancedTableHead
          columns={orderedColumns}
          sorting={
            props.sorting
              ? {
                  orderBy: props.sorting.orderBy,
                  order: props.sorting.order,
                  onRequestSort: (property: keyof T) => {
                    tableContext.onSort(property);
                    props.sorting?.onRequestSort(property);
                  },
                }
              : tableContext.isServerSide
                ? {
                    orderBy: '' as keyof T,
                    order: 'asc',
                    onRequestSort: tableContext.onSort,
                  }
                : undefined
          }
          onSelectAll={(isSelected: boolean) => {
            if (isSelected) {
              selection.handleSelectAll?.(tableContext.actualRows, getRowId);
            } else {
              selection.handleSelectAll?.([], getRowId);
            }
          }}
          isAllSelected={
            selection.selected.length === tableContext.actualRows.length
          }
          isIndeterminate={
            selection.selected.length > 0 &&
            selection.selected.length < tableContext.actualRows.length
          }
          showSelectAll={!!props.onSelectAll}
          isFullscreen={isFullscreen}
          columnResize={!!props.columnResize}
          onColumnResize={columnResize.handleColumnResize}
          columnWidths={columnResize.columnWidths}
          columnReorder={columnReorder}
          tableLayout={tableLayout}
        />

        {/* Table Body - Data rows, loading states, and error states */}
        <EnhancedTableBody>
          {/* Loading State - Show skeleton rows while data is loading */}
          {isLoading &&
            (customLoadingComponent || (
              <TableLoadingSkeleton
                columns={visibleColumns}
                rowCount={5}
                dense={dense}
                showSelectAll={!!props.onSelectAll}
              />
            ))}

          {/* Error State - Show error message with retry option */}
          {error &&
            (customErrorComponent || (
              <TableErrorState
                error={error}
                onRetry={onRetry}
                columns={visibleColumns}
              />
            ))}

          {/* Data Rows - Render actual table data when loaded and no errors */}
          {!isLoading &&
            !error &&
            paginatedRows.map((row) => {
              const rowId = getRowId(row);
              return (
                <EnhancedTableRow
                  key={rowId}
                  row={row}
                  columns={columnReorder ? orderedColumns : visibleColumns}
                  isSelected={selection.selected.includes(rowId)}
                  isExpanded={!!expansion.expanded[rowId]}
                  onSelectRow={selection.handleSelect}
                  onExpandRow={() => expansion.onRowExpand(rowId)}
                  renderExpandedContent={renderExpandedContent}
                  getRowId={getRowId}
                  collapsible={!!collapsible}
                  isFullscreen={!!isFullscreen}
                  showSelection={!!props.onSelectAll}
                  onContextMenu={
                    contextMenu.length > 0 ? handleContextMenu : undefined
                  }
                  inlineEditHook={inlineEdit}
                  inlineEditTriggerMode={props.inlineEdit?.triggerMode}
                  keyboardNavigation={
                    keyboardNavigation?.enabled && keyboardNav
                      ? {
                          ...keyboardNav,
                          config: keyboardNavigation,
                          clearSelection: () => {
                            selection.handleSelectAll?.([], getRowId);
                          },
                        }
                      : undefined
                  }
                  rowIndex={paginatedRows.indexOf(row)}
                  columnWidths={columnResize.columnWidths}
                  tableLayout={tableLayout}
                />
              );
            })}
        </EnhancedTableBody>

        {/* Footer Section - Summary values and total row count */}
        {footer?.enabled && footer.position !== 'separate' && (
          <EnhancedTableFooter
            rows={tableContext.actualRows}
            columns={visibleColumns}
            config={footer}
            dense={dense}
            showSelection={!!props.onSelectAll}
            showExpansion={!!renderExpandedContent}
          />
        )}
      </EnhancedTableContainer>

      {/* Standalone Footer Section - When position is 'separate' or 'both' */}
      {footer?.enabled &&
        (footer.position === 'separate' || footer.position === 'both') && (
          <TableFooterSection rows={tableContext.actualRows} config={footer} />
        )}

      {/* Pagination Section - Page navigation and rows per page controls */}
      {(props.pagination ||
        tableContext.serverData?.queryParams.pagination) && (
        <EnhancedTablePagination
          pagination={
            tableContext.serverData?.queryParams.pagination
              ? {
                  page: tableContext.serverData.queryParams.pagination.page,
                  rowsPerPage:
                    tableContext.serverData.queryParams.pagination.pageSize,
                  count: tableContext.serverData.total,
                  onPageChange,
                  onRowsPerPageChange,
                }
              : props.pagination || {
                  page: 0,
                  rowsPerPage: 10,
                  count: 0,
                  onPageChange: () => {
                    /* No-op for default pagination */
                  },
                  onRowsPerPageChange: () => {
                    /* No-op for default pagination */
                  },
                }
          }
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      {/* Context Menu - Right-click menu for row actions */}
      {contextMenu.length > 0 && contextMenuRow && contextMenuRowId && (
        <ContextMenu
          row={contextMenuRow}
          rowId={contextMenuRowId}
          menuItems={contextMenu}
          anchorPosition={
            contextMenuAnchor
              ? {
                  top: contextMenuAnchor.mouseY,
                  left: contextMenuAnchor.mouseX,
                }
              : undefined
          }
          open={Boolean(contextMenuAnchor)}
          onClose={handleContextMenuClose}
        />
      )}
    </TableProvider>
  );

  /**
   * Conditional rendering based on features enabled:
   * - If column reordering is enabled, wrap with DndContext for drag-and-drop
   * - If keyboard navigation is enabled, add keyboard event handlers
   * - Otherwise, render basic table content
   */
  if (columnReorder) {
    return (
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleColumnDragEnd}
      >
        <SortableContext
          items={orderedColumns.map((col) => String(col.id))}
          strategy={horizontalListSortingStrategy}
        >
          <div
            onKeyDown={
              keyboardNavigation?.enabled
                ? keyboardNav?.handleKeyDown
                : undefined
            }
            tabIndex={keyboardNavigation?.enabled ? 0 : undefined}
          >
            {tableContent}
          </div>
        </SortableContext>
      </DndContext>
    );
  }

  return (
    <div
      onKeyDown={
        keyboardNavigation?.enabled ? keyboardNav?.handleKeyDown : undefined
      }
      tabIndex={keyboardNavigation?.enabled ? 0 : undefined}
    >
      {tableContent}
    </div>
  );
}

EnhancedTable.displayName = 'EnhancedTable';
